// "Griglia viti": rows/cols (linked to the admissible maximum until edited), s1/s2, centre offset, spreading,
// staggering, the "Calcola numero massimo di viti" box and the grid limits.
import { memo, useId, useState, type FC } from "react";
import { Button } from "../../../components/ds2/Button";
import { FormField } from "../../../components/ds2/FormField";
import { MessageBox } from "../../../components/ds2/MessageBox";
import { Numeric } from "../../../components/ds2/Numeric";
import { Switch } from "../../../components/ds2/Switch";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import {
  applyMaxScrewsPatch,
  limitGridTogglePatch,
  type ConnectionType,
  type MaxRowsCols,
  type MaxScrewsInfo,
} from "../../../http/screwConnector/formulas";
import Html from "./Html";
import { useThreeLabels } from "./labels";
import { useMissing } from "./missingFields";
import Note from "./Note";
import { numOrNull, type EngineView } from "./uiState";
import { useNumberDraft } from "./useNumberDraft";
import { useScForm } from "./useScForm";
import styles from "./GridCard.module.css";

type GridCardProps = { view: EngineView };

const GridCard: FC<GridCardProps> = ({ view }) => {
  const disabled = useReadOnly();
  const calc = useI18n().translation.screwConnectorModule.calculation;
  const t = calc.gridSection;
  const switchId = useId();
  const { values, set, apply } = useScForm();
  const draft = useNumberDraft();
  const isMissing = useMissing();
  const { ui } = view;
  const ev = view.values;
  const connectionType: ConnectionType = ev?.materiale1 ?? values.connectionType;
  const labels = useThreeLabels(connectionType);
  // the box shows the maximum computed when the button was pressed (it does not follow later changes, as the HTML)
  const [maxResult, setMaxResult] = useState<{ info: MaxScrewsInfo; best: MaxRowsCols } | null>(null);

  const autoSpacing = ev ? ev.autoSpacing : values.autoSpacing;
  const limitOn = ev ? ev.limitGriglia : values.limitGridToMax;
  const rows = ev ? numOrNull(ev.rows) : values.gridRows;
  const cols = ev ? numOrNull(ev.cols) : values.gridCols;

  return (
    <>
      <div className={styles.row}>
        <FormField label={t.rows} footer={{ type: "info", message: t.rowsUnit }} disabled={disabled}>
          {({ labelId, disabled }) => (
            <Numeric
              id={labelId}
              name="rows"
              decimalScale={0}
              disabled={disabled}
              isInvalid={isMissing("rows", rows === null)}
              {...draft("rows", rows, (v) => apply({ gridRows: v, gridRowsLinkedToMax: false }))}
            />
          )}
        </FormField>
        <FormField label={labels.colsLabel} footer={{ type: "info", message: t.rowsUnit }} disabled={disabled}>
          {({ labelId, disabled }) => (
            <Numeric
              id={labelId}
              name="cols"
              decimalScale={0}
              disabled={disabled}
              isInvalid={isMissing("cols", cols === null)}
              {...draft("cols", cols, (v) => apply({ gridCols: v, gridColsLinkedToMax: false }))}
            />
          )}
        </FormField>
      </div>
      {ui.visible.cltFilaNote ? <Note html={t.cltRowNote} /> : null}

      {/* the engine's warnings (spacing under the minimum) in place of the fields' info footer */}
      <div className={styles.row}>
        <FormField
          label="s1"
          footer={ui.texts.s11Warn ? { type: "warning", message: ui.texts.s11Warn } : { type: "info", message: t.s1Unit }}
          disabled={disabled || ui.disabled.s11}
        >
          {({ labelId, disabled }) => (
            <Numeric
              id={labelId}
              disabled={disabled}
              {...draft("s1", ev ? numOrNull(ev.s11) : values.spacingS1, (v) => set("spacingS1", v))}
            />
          )}
        </FormField>
        <FormField
          label="s2"
          footer={
            ui.texts.s12Warn ? { type: "warning", message: ui.texts.s12Warn } : { type: "info", message: labels.s2Unit }
          }
          disabled={disabled || ui.disabled.s12}
        >
          {({ labelId, disabled }) => (
            <Numeric
              id={labelId}
              disabled={disabled}
              {...draft("s2", ev ? numOrNull(ev.s12) : values.spacingS2, (v) => set("spacingS2", v))}
            />
          )}
        </FormField>
      </div>

      <div className={styles.row}>
        <FormField label={t.offsetX} footer={{ type: "info", message: "mm" }} disabled={disabled}>
          {({ labelId, disabled }) => (
            <Numeric
              id={labelId}
              allowNegative
              disabled={disabled}
              {...draft("offsetX", ev ? numOrNull(ev.centroOrizz) : values.gridOffsetX, (v) =>
                apply({ gridOffsetX: v ?? 0, gridOffsetLinkedToMax: false }),
              )}
            />
          )}
        </FormField>
        <FormField label={t.offsetY} footer={{ type: "info", message: "mm" }} disabled={disabled}>
          {({ labelId, disabled }) => (
            <Numeric
              id={labelId}
              allowNegative
              disabled={disabled}
              {...draft("offsetY", ev ? numOrNull(ev.centroVert) : values.gridOffsetY, (v) =>
                apply({ gridOffsetY: v ?? 0, gridOffsetLinkedToMax: false }),
              )}
            />
          )}
        </FormField>
      </div>

      <Switch
        id={`${switchId}-spread`}
        checked={ev ? ev.spreadViti : values.spreadScrews}
        disabled={disabled || ui.disabled.spreadViti}
        onChange={(v) => set("spreadScrews", v)}
      >
        {t.spread}
      </Switch>
      {ui.texts.spreadNote ? <Note html={ui.texts.spreadNote} /> : null}
      <Switch
        id={`${switchId}-stagger`}
        checked={ev ? ev.sfalsaViti : values.staggerRows}
        disabled={disabled}
        onChange={(v) => set("staggerRows", v)}
      >
        <Html text={t.stagger} />
      </Switch>
      {ui.visible.sfalsaDueLatiRow ? (
        <Switch
          id={`${switchId}-staggerTwoSides`}
          checked={ev ? ev.sfalsaDueLati : values.staggerTwoSides}
          disabled={disabled}
          onChange={(v) => set("staggerTwoSides", v)}
        >
          <Html text={t.staggerTwoSides} />
        </Switch>
      ) : null}

      {/* no result while the maximum cannot be computed (incomplete data), as the HTML */}
      <Button
        variant="secondary"
        disabled={disabled}
        onClick={() => {
          if (view.maxScrews && view.maxRowsCols) setMaxResult({ info: view.maxScrews, best: view.maxRowsCols });
        }}
      >
        {t.maxScrews}
      </Button>
      {maxResult ? (
        <div className={styles.maxResult}>
          {/* no admissible arrangement: a warning */}
          <MessageBox type={maxResult.info.total > 0 ? "informative" : "warning"}>
            <Html text={maxResult.info.html} />
          </MessageBox>
          {maxResult.info.total > 0 ? (
            <Button
              variant="secondary"
              disabled={disabled}
              onClick={() => apply(applyMaxScrewsPatch(maxResult.best, autoSpacing))}
            >
              {t.applyMax}
            </Button>
          ) : null}
        </div>
      ) : null}

      <Switch
        id={`${switchId}-limitGrid`}
        checked={limitOn}
        disabled={disabled}
        onChange={(checked) => apply(limitGridTogglePatch(view.maxRowsCols, checked))}
      >
        {t.limitGrid}
        {ui.visible.limitGrigliaWarn ? (
          <>
            {" "}
            <span className={styles.inlineWarn}>{calc.notes.limitGrid}</span>
          </>
        ) : null}
      </Switch>
      <Switch
        id={`${switchId}-limitGrazing`}
        checked={ev ? ev.limitaGrigliaRadente : values.limitGridDiagonalAtGrazingAngle}
        disabled={disabled}
        onChange={(v) => set("limitGridDiagonalAtGrazingAngle", v)}
      >
        <Html text={t.limitGrazing} />
        {ui.texts.limitaGrigliaRadenteWarn ? (
          <>
            {" "}
            <span className={styles.inlineWarn}>{ui.texts.limitaGrigliaRadenteWarn}</span>
          </>
        ) : null}
      </Switch>
    </>
  );
};

// Memoized: it re-renders through its own props (the engine view among them) and contexts, not whenever its parent
// does.
export default memo(GridCard);
